import React, { useState } from 'react';

import { Dispatch } from 'redux';
import { connect, useDispatch } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';
import { useLocation } from 'react-router';

// Store
import AppState from '@webapp/store/state';
import { CloseMenu, OpenMenu } from '@webapp/store/ui/action-creators';
import {
  CleanMerchStore,
  ListArtistMerch,
  UpdateIsNewMerch,
} from '@webapp/store/merch/action-creators';
import { GetCatalog } from '@webapp/store/catalog/action-creators';

// Services
import { MerchProduct } from '@services/merch/types/api/merch-product';

// Types
import CatalogRelease from '@common/types/catalog/catalog-release';
import CatalogFilter from '@common/types/catalog/catalog-filter-type';

// Components
import Flex from '@components/layout/flex';
import TabsNav, { TabProp } from '@components/tabs-nav';

import CatalogNFT from './nft-tab';
import CatalogTab from './catalog-tab';
import CatalogMerch from './merch-tab';
import CatalogSearch from './catalog-search';

import CatalogMenu from '@webapp/controllers/menus/catalog-menu';
import CatalogTransferMenu from '@webapp/controllers/menus/catalog-transfer-menu';

import MerchMenuSuccess from '@webapp/pages/merch/merch-subscription-success/merch-menu-success';

declare type CatalogProps = {
  releases: CatalogRelease[];
  products: MerchProduct[];
  artistId: string;
  isMobile: boolean;
  isNewMerch: boolean;
  closeMenu: () => CloseMenu;
  getCatalog: (artistId: string) => GetCatalog;
  listArtistMerch: (artistId: string) => ListArtistMerch;
  cleanMerchStore: () => CleanMerchStore;
  updateIsNewMerch: (isNewMerch: boolean) => UpdateIsNewMerch;
};

const mapStateToProps = (state: AppState) => ({
  releases: state.catalog.releases.items,
  products: state.merch.artistMerch || [],
  isNewMerch: state.merch.isNewMerch || false,
  isMobile: state.ui.mobile,
  artistId: state.artist.active?.id as string,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  closeMenu: () => dispatch(new CloseMenu()),
  getCatalog: (artistId: string) =>
    dispatch(new GetCatalog({ type: 'COLLABORATOR', artistId })),
  listArtistMerch: (artistId: string) =>
    dispatch(new ListArtistMerch(artistId)),
  cleanMerchStore: () => dispatch(new CleanMerchStore()),
  updateIsNewMerch: (isNewMerch: boolean) =>
    dispatch(new UpdateIsNewMerch(isNewMerch)),
});

export type CatalogTabs = 'releases' | 'merch' | 'nft';
const initialTab = 0;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(function Catalog({
  artistId,
  releases,
  products,
  isMobile,
  isNewMerch,
  getCatalog,
  listArtistMerch,
  cleanMerchStore,
  updateIsNewMerch,
}: CatalogProps) {
  const [releasesListLength, setReleasesListLength] = useState<number>(0);
  const [merchListLength, setMerchListLength] = useState<number>(0);
  const [nftListLength] = useState<number>(0);

  const tabsData: TabProp<CatalogTabs>[] = React.useMemo(
    () => [
      {
        label: (
          <Translate
            id="CATALOG.TABS.RELEASES"
            data={{ amount: releasesListLength || 0 }}
          />
        ),
        key: 'releases' as CatalogTabs,
      },
      {
        label: (
          <Translate
            id="CATALOG.TABS.MERCH"
            data={{ amount: merchListLength || 0 }}
          />
        ),
        key: 'merch' as CatalogTabs,
        hasDot: isNewMerch,
      },
      {
        label: (
          <Translate
            id="CATALOG.TABS.NFT"
            data={{ amount: nftListLength || 0 }}
          />
        ),
        key: 'nft' as CatalogTabs,
        disabled: true,
        soon: true,
      },
    ],
    [releasesListLength, merchListLength, nftListLength, isNewMerch]
  );

  const [activeTab, setActiveTab] = useState<CatalogTabs>(
    tabsData[initialTab].key
  );
  const [filterOption, setFilterOption] = React.useState<CatalogFilter>({});
  const [hasChanges, setHasChanges] = React.useState(false);
  const urlParams = new URLSearchParams(useLocation().search);
  const type = urlParams.get('type');
  const productId = urlParams.get('productId');
  const orderId = urlParams.get('orderId');

  React.useEffect(() => {
    getCatalog(artistId);
  }, [getCatalog, artistId]);

  React.useEffect(() => {
    if (type === 'releases') {
      setActiveTab(tabsData[0].key);
    } else if (type === 'merch') {
      setActiveTab(tabsData[1].key);
    } else if (type === 'nft') {
      setActiveTab(tabsData[2].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const keys = Object.keys(filterOption);
    if (
      keys.some(
        (key) =>
          !!filterOption[key].length ||
          !!Object.values(filterOption[key]).length
      )
    ) {
      setHasChanges(true);
    } else {
      setHasChanges(false);
    }
  }, [filterOption]);

  React.useEffect(() => {
    cleanMerchStore();
    if (artistId) {
      listArtistMerch(artistId);
    }
  }, [artistId, listArtistMerch, cleanMerchStore]);

  React.useEffect(() => {
    if (setMerchListLength) {
      return setMerchListLength(products.length);
    }
  }, [products]);

  const handleTabClick = (value: CatalogTabs) => {
    if (value === 'merch') {
      updateIsNewMerch(false);
    }
    setActiveTab(value);
    setFilterOption({});
    setHasChanges(false);
  };

  const activeSection = React.useMemo(() => {
    switch (activeTab) {
      case 'releases':
        return (
          <CatalogTab
            list={releases}
            isReleases
            allReleases={releases}
            setReleasesListLength={setReleasesListLength}
            filterOption={filterOption}
          />
        );
      // TODO: DEVTEAM-603 - CatalogTabs doesn't have a value of recordings, This needs to be reviewed to see if we need it.
      // case 'recording':
      //   if (masters?.length) {
      //     <CatalogTab sortingKey={sorting} list={masters} isReleases={false} />;
      //   } else {
      //     return;
      //   }
      case 'merch':
        return <CatalogMerch products={products} filterOption={filterOption} />;

      case 'nft':
        return <CatalogNFT />;

      default:
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, releases, products, filterOption]);

  const dispatch = useDispatch();

  React.useEffect(() => {
    if (type === 'merch' && productId) {
      dispatch(new OpenMenu('MERCH_SUCCESS_MENU'));
    }
  }, [type, productId, dispatch]);

  return (
    <>
      <CatalogContainer>
        <Flex
          className="mb-40"
          alignItems="center"
          justifyContent="space-between"
          gap={20}
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <TabsNav
            className="tab-nav"
            tabs={tabsData}
            currentTab={tabsData.findIndex((tab) => tab.key === activeTab)}
            onChange={(tab) => handleTabClick(tab.key as CatalogTabs)}
            bigFont
            overflow={isMobile}
          />
          <Flex
            justifyContent={isMobile ? 'space-between' : 'flex-end'}
            alignItems="center"
            gap={10}
          >
            <CatalogSearch
              section={activeTab}
              filterOption={filterOption}
              setFilterOption={setFilterOption}
            />
            <CatalogMenu
              section={activeTab}
              hasChanges={hasChanges}
              filterOption={filterOption}
              setFilterOption={setFilterOption}
            />
            <CatalogTransferMenu />
          </Flex>
        </Flex>
        {activeSection}
      </CatalogContainer>
      <MerchMenuSuccess productId={productId} orderId={orderId} />
    </>
  );
});

const CatalogContainer = styled.div`
  max-width: none;
  min-height: 100vh;
  padding: 24px 24px 55px;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    padding: 55px;
  }

  ${({ theme }) => `@media screen and ${theme.device.laptop}`} {
    padding: 90px 180px;
  }

  ${({ theme }) => `@media screen and ${theme.device.desktopL}`} {
    padding: 130px 320px;
  }

  .tab-nav {
    padding-inline: 24px;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      padding-inline: 0;
    }
  }
`;
